// ProtectedRoute.tsx — wrapper de rota que exige sessão autenticada.
//
// - status='loading'  → renderiza fallback (default: skeleton)
// - status='unauthenticated' → redireciona para /login com state.from
// - status='authenticated' → renderiza children
//
// Opcional: `requireGroups` força user pertencer a um dos grupos.

import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useSession, hasGroup } from '@/lib/auth/useSession';
import { Container } from '@/components/ui/Container';
import { Skeleton } from '@/components/ui/Skeleton';

interface ProtectedRouteProps {
  children: ReactNode;
  /** Se definido, só permite acesso se user pertencer a algum dos grupos. */
  requireGroups?: string[];
  /** Componente de loading customizado. */
  fallback?: ReactNode;
}

export function ProtectedRoute({ children, requireGroups, fallback }: ProtectedRouteProps) {
  const { status, user } = useSession();
  const location = useLocation();

  if (status === 'loading') {
    return (
      fallback ?? (
        <Container size="md" className="py-12">
          <Skeleton shape="rect" className="h-8 w-1/3" />
          <Skeleton shape="rect" className="mt-4 h-4 w-2/3" />
          <Skeleton shape="rect" className="mt-2 h-4 w-1/2" />
        </Container>
      )
    );
  }

  if (status === 'unauthenticated' || !user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (requireGroups && !hasGroup(user.groups, ...requireGroups)) {
    return <Navigate to="/forbidden" replace />;
  }

  return <>{children}</>;
}
